import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { detectBrowserLanguage, detectUrlLanguage } from './utils/languageDetection';
import { getStoredLanguage, setStoredLanguage } from './utils/languageStorage';
import {
  DEFAULT_LANGUAGE,
  type SupportedLanguage,
  type TranslationKey,
} from './types';
import { setCurrentLanguage, translate } from './translate';

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

const resolveInitialLanguage = (): SupportedLanguage => {
  return detectUrlLanguage() || getStoredLanguage() || detectBrowserLanguage() || DEFAULT_LANGUAGE;
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(
    resolveInitialLanguage()
  );

  useEffect(() => {
    setCurrentLanguage(language);
    setStoredLanguage(language);

    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = (next: SupportedLanguage) => {
    setLanguageState(next);
  };

  const t = useMemo(
    () => (key: TranslationKey, values?: Record<string, string | number>) =>
      translate(key, values, language),
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, t]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return ctx;
}
